import { bodySegments, type CommentMention } from "@estelle/domain";
import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

/**
 * A comment's text as people read it (#448): line breaks kept, a mention shown
 * as the person's name, and a web address a link.
 *
 * Nothing in the text is ever parsed as markup. It is split into plain strings
 * and React elements, so what someone typed — angle brackets, a pasted script —
 * is shown as typed and never injected. Only `http` and `https` addresses become
 * links, so a `javascript:` address stays text, and every link opens in a new
 * tab with no opener.
 */

/** An address, up to the next space or a character no address contains. */
const ADDRESS = /https?:\/\/[^\s<>"']+/g;
/** Punctuation that ends a sentence rather than an address. */
const TRAILING = /[.,;:!?)\]]+$/;

function Linkified({ text }: { text: string }) {
  const parts: ReactNode[] = [];
  let last = 0;
  for (const match of text.matchAll(ADDRESS)) {
    const address = match[0].replace(TRAILING, "");
    if (address === "") continue;
    if (match.index > last) parts.push(text.slice(last, match.index));
    parts.push(
      <a
        key={match.index}
        href={address}
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary-strong break-all underline underline-offset-2"
      >
        {address}
      </a>,
    );
    last = match.index + address.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return <>{parts}</>;
}

export function CommentBody({
  body,
  mentions,
  className,
}: {
  body: string;
  mentions: CommentMention[];
  className?: string;
}) {
  const names = new Map(mentions.map((mention) => [mention.userId, mention]));
  return (
    <p className={cn("text-sm break-words whitespace-pre-wrap", className)}>
      {bodySegments(body).map((segment, index) => {
        if (segment.kind === "text") {
          return <Linkified key={index} text={segment.text} />;
        }
        const mention = names.get(segment.userId);
        // A token that names no one the server resolved is shown as it was
        // written, not hidden: it is the author's text.
        if (!mention) return <span key={index}>@[{segment.userId}]</span>;
        return (
          <span
            key={index}
            className="bg-primary/10 text-primary-strong rounded px-1 font-medium"
          >
            @{mention.name}
          </span>
        );
      })}
    </p>
  );
}
